<template>
  <Dialog
    ref="dialog"
    :closable="closable"
    :close-on-escape="false"
    :dismissable-mask="dismissableMask"
    :draggable="draggable"
    :header="header"
    :modal="modal"
    :pt="mergedPt"
    :style="style"
    :visible="visible"
    v-bind="$attrs"
    @update:visible="$emit('update:visible', $event)"
  >
    <template
      v-if="$slots.header"
      #header
    >
      <slot name="header" />
    </template>

    <slot />

    <template
      v-if="$slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { Dialog } from 'primevue'
import { type ComponentPublicInstance, computed, useTemplateRef } from 'vue'

import {
  DEFAULT_TWN_DIALOG_PROPS,
  type TwnDialogProps
} from '@/components/atoms/prime/dialog/twn-dialog/TwnDialog.types'
import usePrimeVuePtMerge from '@/composables/use-prime-vue-helpers/usePrimeVuePtMerge'
import { useTopmostEscape } from '@/composables/use-topmost-escape/useTopmostEscape'

const props = withDefaults(defineProps<TwnDialogProps>(), DEFAULT_TWN_DIALOG_PROPS)

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const defaultPt = {
  root: {
    class: 'rounded-xl'
  }
}

const mergedPt = computed(() => {
  if (!props.pt) return defaultPt

  return usePrimeVuePtMerge(defaultPt, props.pt).value
})

// PrimeVue's Dialog exposes its mask element; the top-most check reads the z-index on it.
const dialogRef = useTemplateRef<ComponentPublicInstance & { mask?: HTMLElement | null }>('dialog')

// Escape closes this dialog only when nothing — a confirm, say — sits above it.
useTopmostEscape({
  isEnabled: () => Boolean(props.visible) && props.closeOnEscape,
  getMask: () => dialogRef.value?.mask,
  close: () => emit('update:visible', false)
})
</script>
